import { Select, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import uuidv4 from 'uuid/v4';
import { Ajax } from '../../components/Ajax';
import { ApiUrl } from '../../project/Defines.js';
import { useCustomEvent } from '../../project/utilities';
import { useSWCache } from '../../Shared/SWCache';

const { Option, OptGroup } = Select;

/**
 * specific case of DropDownValueList with 'multiple' mode
 * @param {any} props valuelist, items, parentValue, additionalData, reloadEventName, ...restprops
 */
export function MultiSelectValueList(props) {
  return <DropDownValueList {...props} mode='multiple' />;
}

/**
 * custom DropDownValueList working with backend value lists and custom given items
 * @param {object} props   valuelist, mode, items, parentValue, additionalData, reloadEventName, ...restprops
 */
export function DropDownValueList(props) {
  const [loaded, setLoaded] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const { value, restrictCustomValue, allowClear, valuelist, mode, size, items, extraItems, parentValue, additionalData, record, reloadEventName, disableCach, clearMinutes, culture, autoSelectFirst, dispatch, searchOptions, searchServerSide, textAsValue, ...restprops } = props;
  const ref = useRef(false);
  const { getCacheData, setCacheData } = useSWCache({ key: `${culture}_${valuelist}_${parentValue}_${additionalData}`, isClearOnLogout: typeof clearMinutes === 'undefined', clearMinutes: disableCach ? 0 : clearMinutes });
  const manageValue = useCallback((data) => {

    if ((restrictCustomValue && !mode) && value && !data.some(item => item[textAsValue ? 'text' : 'value'] === value)) {
      if (props.onChange) {
        props.onChange(autoSelectFirst ? data[0].value : '');
      }
    } else if (autoSelectFirst && !value) {
      if (props.onChange) {
        props.onChange(data[0].value);
      }
    }

  }, [value, autoSelectFirst, textAsValue, restrictCustomValue, mode, props]);

  const loadData = useCallback(() => {
    if (valuelist) {
      if (typeof valuelist === 'string') {
        setLoaded(false);
        Ajax.post({
          url: ApiUrl.ValueList,
          data: {
            valueList: valuelist,
            parentValue: parentValue || "",
            additionalData: additionalData || "",
            record: record || false
          },
          success: function (response) {
            if (response && ref.current) {
              const data = response;
              for (const item of data) {
                item.key = item.uuid;
              }
              setDataSource(data);
              setLoaded(true);
              manageValue(data);
              !disableCach && setCacheData(data);
            }
          },
          error: function () {
            setDataSource([]);
            setLoaded(true);
          }
        });
      } else {
        setDataSource(valuelist);
        setLoaded(true);
        manageValue(valuelist);
      }
    }
  }, [valuelist, parentValue, additionalData, record, autoSelectFirst, disableCach, setCacheData, manageValue]);// eslint-disable-line react-hooks/exhaustive-deps

  useCustomEvent(reloadEventName || 'reload_valuelist_' + valuelist, loadData);

  useEffect(() => {
    ref.current = true;
    if (items) {
      setDataSource([...items]);
      setLoaded(true);
      manageValue(items);
    }
    else if (disableCach) {
      loadData();
    } else {
      getCacheData().then((data) => {
        if (ref.current) {
          if (data) {
            setDataSource(data);
            setLoaded(true);
            manageValue(data);
          } else {
            loadData();
          }
        }
      });
    }
    return () => { ref.current = false; };
  }, [additionalData, valuelist, parentValue, record]);// eslint-disable-line react-hooks/exhaustive-deps

  const filterOption = (input, option) => {
    let text = searchOptions.caseSensitive ? option.text : option.text?.toLowerCase();
    let searchInput = searchOptions.caseSensitive ? input : input?.toLowerCase();
    if (searchOptions.noAccent) {
      text = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      searchInput = searchInput.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }
    return !searchInput || (text && (searchOptions.contains ? text.includes(searchInput) : text.startsWith(searchInput)));
  };

  const dataSourceWithKeys = useMemo(() => {
    return dataSource?.map(item => ({
      ...item,
      key: item.key || item.uuid || uuidv4()
    }));
  }, [dataSource]);

  const extraItemsWithKeys = useMemo(() => {
    return extraItems?.map(item => ({
      ...item,
      key: item.key || item.uuid || uuidv4()
    }));
  }, [extraItems]);

  const dataSourceOptions = dataSourceWithKeys?.map((item) =>
    <Option key={item.key} value={textAsValue ? item.text : item.value} text={item.text}
  >
      {item.text}
    </Option>
  );
  return <Select
      allowClear={allowClear}
      size={size || "default"}
      mode={mode}
      virtual={false}
      value={value}
      {...restprops}
      onChange={(e) => props.onChange(e, dataSource)}
      {...(mode === "tags" ? {} : { filterOption: searchOptions && !searchServerSide && filterOption })}
      showSearch={!!searchOptions || mode === "tags"}
      //disabled={true}

    >
      {extraItemsWithKeys?.map((item) =>
        <Option 
          key={item.key} value={textAsValue ? item.text : item.value} text={item.text}
        >
          {item.text}
        </Option>)
      }
      {(loaded || mode === 'tags') ? (extraItems && dataSource?.length ?
        <OptGroup label={<separator horizontal=""
          />}> 
          {dataSourceOptions}
        </OptGroup> : dataSourceOptions) : <div style={{
          "margin": "auto",
          "background": "transparent"  }}><Spin size="small" /></div>}
    </Select>;
}

DropDownValueList.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
    restrictCustomValue: PropTypes.bool,
    allowClear: PropTypes.bool,
    valuelist: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    mode: PropTypes.oneOf(['multiple', 'tags']),
    size: PropTypes.oneOf(['small', 'middle', 'large']),
    items: PropTypes.array,
    extraItems: PropTypes.array,
    parentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    additionalData: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    record: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    reloadEventName: PropTypes.string,
    disableCach: PropTypes.bool,
    clearMinutes: PropTypes.number,
    culture: PropTypes.string,
    autoSelectFirst: PropTypes.bool,
    dispatch: PropTypes.func,
    searchOptions: PropTypes.shape({
        caseSensitive: PropTypes.bool,
        noAccent: PropTypes.bool,
        contains: PropTypes.bool
    }),
    searchServerSide: PropTypes.bool,
    textAsValue: PropTypes.bool,
    onChange: PropTypes.func
};

MultiSelectValueList.propTypes = DropDownValueList.propTypes;

export default connect(state => ({ culture: state.culture }))(DropDownValueList);;